import React, { useState } from 'react';
import { Avatar } from '../components/Avatar';
import AdminBanner from '../components/navigation/AdminBanner';
import { Sidebar } from '../components/navigation/Sidebar';
import EventBannerStrip from '../components/events/EventBannerStrip';

const SIDEBAR_COLLAPSED_KEY = 'dashboard_sidebarCollapsed';

interface LayoutProps {
    children: React.ReactNode;
    /**
     * When true, the main pane does not scroll externally - children take the
     * remaining viewport height and manage their own internal scrolling.
     * Used by viewport-locked pages like the approve flow.
     */
    noScroll?: boolean;
    /**
     * When true, force the left nav into collapsed mode and disable the
     * toggle for the lifetime of this layout. Used by content-heavy routes
     * (e.g. the approve flow) that need every horizontal pixel they can get
     * without changing the user's persisted preference.
     */
    forceCollapsed?: boolean;
}

export const SidebarLayout: React.FC<LayoutProps> = ({
    children,
    noScroll = false,
    forceCollapsed = false
}) => {
    const [userCollapsed, setUserCollapsed] = useState(() => {
        return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    });

    // forceCollapsed wins over the user's preference but never overwrites it.
    const collapsed = forceCollapsed || userCollapsed;

    const handleToggle = () => {
        if (forceCollapsed) return;
        const next = !userCollapsed;
        setUserCollapsed(next);
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
    };

    const mainClass = noScroll
        ? 'flex-1 overflow-hidden flex flex-col min-w-0'
        : 'flex-1 overflow-auto scrollbar-hide';

    // When noScroll, children must claim flex-1 + min-h-0 themselves to fill
    // the remaining space; the avatar overlay container becomes a flex-1
    // wrapper instead of a relative-only block.
    const contentWrapperClass = noScroll ? 'relative flex-1 min-h-0 flex flex-col' : 'relative';

    return (
        <div className="flex w-full h-screen bg-gray-100">
            <Sidebar collapsed={collapsed} onToggle={handleToggle} />
            <main className={mainClass}>
                {/* Admin banner at the very top (if visible - both AdminBanner and
            EventBannerStrip render null when not applicable, so this never
            reserves space for nothing). Sticks while scrolling in the default
            layout; flex-shrink-0 (still visible, just not sticky - there's no
            scroll to stick against) in the noScroll layout, so viewport-locked
            pages never hide it. */}
                <div className={noScroll ? 'flex-shrink-0' : 'sticky top-0 z-20'}>
                    <AdminBanner />
                    <EventBannerStrip />
                </div>
                {/* Content area with support phone + avatar overlayed in the top-right */}
                <div className={contentWrapperClass}>
                    <div className="absolute top-4 right-8 z-10 flex items-center gap-3">
                        <div className="w-10 h-10">
                            <Avatar />
                        </div>
                    </div>
                    {children}
                </div>
            </main>
        </div>
    );
};
